import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Ornament } from "../components/Ornament";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";

export default function VerifyEmail() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const { user, setUser, refresh } = useAuth();
  const [state, setState] = useState("verifying");
  const [error, setError] = useState("");
  const [resending, setResending] = useState(false);
  const [resent, setResent] = useState(false);

  useEffect(() => {
    const token = params.get("token");
    const flow = params.get("flow");
    if (!token) { setState("err"); setError("This confirmation link is missing its code."); return; }
    (async () => {
      try {
        await api.post("/auth/verify-email", { token });
        try {
          const me = await api.get("/auth/me");
          setUser(me.data);
        } catch (e) { /* not signed in on this browser is fine */ }
        setState("ok");
        // Route directly to Day 1 — no intro page, no dashboard detour.
        setTimeout(() => navigate("/story-week/day/1", { replace: true }), 1200);
      } catch (e) {
        setState("err");
        setError(e?.response?.data?.detail || "This confirmation link is invalid or has expired.");
      }
    })();
    // eslint-disable-next-line
  }, []);

  const resend = async () => {
    setResending(true);
    try {
      await api.post("/auth/resend-verification", {
        origin_url: window.location.origin,
        flow: params.get("flow") || undefined,
      });
      setResent(true);
    } catch (e) {
      setError(e?.response?.data?.detail || "Couldn't resend just now. Please try again in a moment.");
    } finally {
      setResending(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col" style={{ background: "#111D33" }} data-testid="verify-email-page">
      <main className="flex-1 flex items-center justify-center px-6 py-10">
        <div className="w-full max-w-md p-8 sm:p-10 bg-[#FAF7F2] text-center" style={{ borderRadius: 4 }}>
          <p className="label-ui text-[11px] text-[#B8973A]">Email confirmation</p>
          <Ornament className="my-6" />
          {state === "verifying" && (
            <p className="italic text-[#1B2A4A]" style={{ fontFamily: "Cormorant Garamond, serif", fontSize: "1.2rem" }}>
              Confirming your email…
            </p>
          )}
          {state === "ok" && (
            <>
              <h1 className="text-3xl mb-3" style={{ fontFamily: "Cormorant Garamond, serif" }}>Thank you.</h1>
              <p className="text-[#1B2A4A]" style={{ fontFamily: "Georgia, serif", fontSize: "18px", lineHeight: 1.7 }}>
                Your email is confirmed. Opening Day One of your Story Week now.
              </p>
            </>
          )}
          {state === "err" && (
            <>
              <h1 className="text-3xl mb-3" style={{ fontFamily: "Cormorant Garamond, serif" }}>We couldn't confirm this link.</h1>
              <p className="text-[#8C8278] mb-6" style={{ fontFamily: "Georgia, serif", fontSize: "15px", lineHeight: 1.6 }} data-testid="verify-error">
                {error}
              </p>

              {user && !user.email_verified && (
                <div className="mb-6" data-testid="verify-resend-block">
                  {resent ? (
                    <p className="text-[#1B2A4A]" style={{ fontFamily: "Georgia, serif", fontSize: "15px", lineHeight: 1.6 }} data-testid="verify-resent">
                      A fresh confirmation email is on its way to <strong>{user.email}</strong>. Open the newest one from The Living Book.
                    </p>
                  ) : (
                    <button onClick={resend} disabled={resending}
                      className="btn-gold mb-3" style={{ minHeight: 48 }}
                      data-testid="verify-resend-btn">
                      {resending ? "Sending…" : "Send me a fresh confirmation email"}
                    </button>
                  )}
                </div>
              )}

              {user && user.email_verified && (
                <p className="text-[#1B2A4A] mb-6" style={{ fontFamily: "Georgia, serif", fontSize: "15px", lineHeight: 1.6 }}>
                  Good news — your email is already confirmed. This link is just an older copy.
                </p>
              )}

              <button onClick={() => navigate("/story-week/day/1")} className={user && !user.email_verified && !resent ? "hairline-cream bg-white px-5 py-3 text-[#1B2A4A]" : "btn-gold"} style={{ minHeight: 48, borderRadius: 4, fontFamily: "Georgia, serif", fontSize: "13px", letterSpacing: "0.15em" }} data-testid="verify-back-btn">
                Click here to try opening Day One
              </button>
            </>
          )}
        </div>
      </main>
    </div>
  );
}
